/**
 * MagicBI Design System - Typography Components
 * 
 * Complete typography component system with semantic variants,
 * responsive behavior, and accessibility features.
 */

import React from 'react';
import styled, { css } from 'styled-components';
import { typographyMixin } from '../../../tokens/typography';
import type { TypographyCategory, TypographyVariant } from '../../../tokens/typography';

// Base typography props
interface BaseTypographyProps {
  children: React.ReactNode;
  className?: string;
  color?: string;
  align?: 'left' | 'center' | 'right' | 'justify';
  weight?: 'light' | 'regular' | 'medium' | 'semibold' | 'bold' | 'extrabold';
  as?: keyof JSX.IntrinsicElements;
  truncate?: boolean;
  noWrap?: boolean;
}

// Display component props
interface DisplayProps extends BaseTypographyProps {
  variant?: 'large' | 'medium' | 'small';
}

// Heading component props
interface HeadingProps extends BaseTypographyProps {
  level?: 1 | 2 | 3 | 4 | 5;
}

// Body text component props
interface BodyProps extends BaseTypographyProps {
  variant?: 'large' | 'medium' | 'small';
}

// Label component props
interface LabelProps extends BaseTypographyProps {
  variant?: 'large' | 'medium' | 'small';
  htmlFor?: string;
}

// Caption component props
interface CaptionProps extends BaseTypographyProps {
  variant?: 'medium' | 'small';
}

// Code component props
interface CodeProps extends BaseTypographyProps {
  variant?: 'large' | 'medium' | 'small';
  inline?: boolean;
  language?: string;
}

// Base typography styles
const baseTypographyStyles = css<BaseTypographyProps>`
  margin: 0;
  padding: 0;
  
  ${({ color, theme }) => color && css`
    color: ${color === 'inherit' ? 'inherit' : theme.colors.text[color as keyof typeof theme.colors.text] || color};
  `}
  
  ${({ align }) => align && css`
    text-align: ${align};
  `}
  
  ${({ weight }) => weight && css`
    font-weight: ${({ theme }) => theme.typography.body.medium.fontWeight};
  `}
  
  ${({ truncate }) => truncate && css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
  
  ${({ noWrap }) => noWrap && css`
    white-space: nowrap;
  `}
`;

// Display component
const StyledDisplay = styled.h1<DisplayProps>`
  ${baseTypographyStyles}
  
  ${({ variant = 'large' }) => {
    return typographyMixin('display', variant);
  }}
  
  /* Responsive scaling */
  ${({ theme }) => theme.breakpoints.mediaQueries.down.md} {
    ${({ variant = 'large' }) => {
      if (variant === 'large') return typographyMixin('display', 'medium');
      if (variant === 'medium') return typographyMixin('display', 'small');
      return typographyMixin('heading', 'h1');
    }}
  }
  
  ${({ theme }) => theme.breakpoints.mediaQueries.down.sm} {
    ${({ variant = 'large' }) => {
      if (variant === 'large') return typographyMixin('display', 'small');
      if (variant === 'medium') return typographyMixin('heading', 'h1');
      return typographyMixin('heading', 'h2');
    }}
  }
`;

// Heading component
const StyledHeading = styled.h1<HeadingProps>`
  ${baseTypographyStyles}
  
  ${({ level = 1 }) => {
    const headingMap = {
      1: 'h1',
      2: 'h2',
      3: 'h3',
      4: 'h4',
      5: 'h5',
    } as const;
    
    return typographyMixin('heading', headingMap[level as 1 | 2 | 3 | 4 | 5]);
  }}
  
  /* Responsive scaling */
  ${({ theme }) => theme.breakpoints.mediaQueries.down.sm} {
    ${({ level = 1 }) => {
      if (level <= 2) {
        const headingMap = { 1: 'h2', 2: 'h3' } as const;
        return typographyMixin('heading', headingMap[level as 1 | 2]);
      }
      return '';
    }}
  }
`;

// Body text component
const StyledBody = styled.p<BodyProps>`
  ${baseTypographyStyles}
  
  ${({ variant = 'medium' }) => {
    return typographyMixin('body', variant);
  }}
`;

// Label component
const StyledLabel = styled.label<LabelProps>`
  ${baseTypographyStyles}
  
  ${({ variant = 'medium' }) => {
    return typographyMixin('label', variant);
  }}
  
  cursor: pointer;
  display: inline-block;
`;

// Caption component
const StyledCaption = styled.span<CaptionProps>`
  ${baseTypographyStyles}
  
  ${({ variant = 'medium' }) => {
    return typographyMixin('caption', variant);
  }}
  
  color: ${({ theme, color }) => color || theme.colors.text.secondary};
`;

// Code component
const StyledCode = styled.code<CodeProps>`
  ${baseTypographyStyles}
  
  ${({ variant = 'medium' }) => {
    return typographyMixin('code', variant);
  }}
  
  ${({ inline = true, theme }) => inline ? css`
    background-color: ${theme.colors.surface.secondary};
    border: 1px solid ${theme.colors.border.subtle};
    border-radius: ${theme.borders.radius.sm};
    padding: 2px 6px;
    font-size: 0.875em;
  ` : css`
    display: block;
    background-color: ${theme.colors.surface.secondary};
    border: 1px solid ${theme.colors.border.subtle};
    border-radius: ${theme.borders.radius.md};
    padding: ${theme.spacing.semantic.inset.md};
    overflow-x: auto;
    white-space: pre;
  `}
`;

// Export styled components for direct use
export {
  StyledDisplay,
  StyledHeading,
  StyledBody,
  StyledLabel,
  StyledCaption,
  StyledCode,
};

// Display component
export const Display: React.FC<DisplayProps> = ({ 
  variant = 'large', 
  as,
  children,
  ...props 
}) => {
  return (
    <StyledDisplay as={as || 'h1'} variant={variant} {...props}>
      {children}
    </StyledDisplay>
  );
};

// Heading component
export const Heading: React.FC<HeadingProps> = ({ 
  level = 1, 
  as,
  children,
  ...props 
}) => {
  const defaultElement = `h${level}` as keyof JSX.IntrinsicElements;
  
  return (
    <StyledHeading as={as || defaultElement} level={level} {...props}>
      {children}
    </StyledHeading>
  );
};

// Body text component
export const Text: React.FC<BodyProps> = ({ 
  variant = 'medium', 
  as = 'p',
  children,
  ...props 
}) => {
  return (
    <StyledBody as={as || 'p'} variant={variant} {...props}>
      {children}
    </StyledBody>
  );
};

// Label component
export const Label: React.FC<LabelProps> = ({ 
  variant = 'medium',
  children,
  ...props 
}) => {
  return (
    <StyledLabel variant={variant} {...props}>
      {children}
    </StyledLabel>
  );
};

// Caption component
export const Caption: React.FC<CaptionProps> = ({ 
  variant = 'medium',
  children,
  ...props 
}) => {
  return (
    <StyledCaption variant={variant} {...props}>
      {children}
    </StyledCaption>
  );
};

// Code component
export const Code: React.FC<CodeProps> = ({ 
  variant = 'medium',
  inline = true,
  children,
  ...props 
}) => {
  return (
    <StyledCode variant={variant} inline={inline} {...props}>
      {children}
    </StyledCode>
  );
};

// Pre-composed heading components for convenience
export const H1: React.FC<Omit<HeadingProps, 'level'>> = (props) => (
  <Heading level={1} {...props} />
);

export const H2: React.FC<Omit<HeadingProps, 'level'>> = (props) => (
  <Heading level={2} {...props} />
);

export const H3: React.FC<Omit<HeadingProps, 'level'>> = (props) => (
  <Heading level={3} {...props} />
);

export const H4: React.FC<Omit<HeadingProps, 'level'>> = (props) => (
  <Heading level={4} {...props} />
);

export const H5: React.FC<Omit<HeadingProps, 'level'>> = (props) => (
  <Heading level={5} {...props} />
);

export const H6: React.FC<Omit<HeadingProps, 'level'>> = (props) => (
  <Heading level={5} {...props} />
);

// Utility components
export const Strong: React.FC<BaseTypographyProps> = ({ children, ...props }) => (
  <Text as="strong" weight="semibold" {...props}>
    {children}
  </Text>
);

export const Em: React.FC<BaseTypographyProps> = ({ children, ...props }) => (
  <Text as="em" {...props}>
    {children}
  </Text>
);

export const Small: React.FC<BaseTypographyProps> = ({ children, ...props }) => (
  <Caption as="small" variant="small" {...props}>
    {children}
  </Caption>
);

export const Mark: React.FC<BaseTypographyProps> = ({ children, ...props }) => (
  <Text as="mark" {...props}>
    {children}
  </Text>
);

// Export types
export type {
  BaseTypographyProps,
  DisplayProps,
  HeadingProps,
  BodyProps,
  LabelProps,
  CaptionProps,
  CodeProps,
};
